import { RiwayatObatEntri, StatusRiwayatObat } from "@/types/riwayat_obat";
import { BarisResepTerstruktur, barisBaru } from "@/utils/resepTerstruktur";

export const LABEL_STATUS_RIWAYAT_OBAT: Record<StatusRiwayatObat, string> = {
  diberikan: "Diberikan farmasi",
  tidak_ditebus: "Diresepkan · tidak ditebus",
  tidak_diberikan: "Diresepkan · tidak diberikan farmasi",
  menunggu: "Diresepkan · menunggu farmasi",
  tidak_diketahui: "Diresepkan · status farmasi tidak terbaca"
};

export const WARNA_STATUS_RIWAYAT_OBAT: Record<
  StatusRiwayatObat,
  { latar: string; teks: string }
> = {
  diberikan: { latar: "green lighten-5", teks: "green darken-3" },
  tidak_ditebus: { latar: "orange lighten-5", teks: "deep-orange darken-3" },
  tidak_diberikan: { latar: "orange lighten-5", teks: "deep-orange darken-3" },
  menunggu: { latar: "blue-grey lighten-5", teks: "blue-grey darken-3" },
  tidak_diketahui: { latar: "grey lighten-4", teks: "grey darken-3" }
};

export function formatTanggalRiwayat(tanggal: string | null): string {
  if (!tanggal) {
    return "";
  }
  const [tahun, bulan, hari] = tanggal.slice(0, 10).split("-");
  return tahun && bulan && hari ? `${hari}-${bulan}-${tahun}` : tanggal;
}

const NAMA_BULAN = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember"
];

export function formatBulanPartisi(ym: string): string {
  const bulan = Number(ym.slice(2, 4));
  return /^\d{4}$/.test(ym) && bulan >= 1 && bulan <= 12
    ? `${NAMA_BULAN[bulan - 1]} 20${ym.slice(0, 2)}`
    : ym;
}

function formatJumlah(qty: number | null): string {
  if (qty === null || qty === undefined) {
    return "";
  }
  return String(Number(qty.toFixed(3))).replace(".", ",");
}

export function labelStatusRiwayatObat(entri: RiwayatObatEntri): string {
  const terakhir = entri.terakhir;
  const dasar = LABEL_STATUS_RIWAYAT_OBAT[terakhir.status] || terakhir.status;
  return terakhir.status === "diberikan" && terakhir.no_transaksi
    ? `${dasar} · Trx ${terakhir.no_transaksi}`
    : dasar;
}

export function ringkasanRiwayatObat(entri: RiwayatObatEntri): string {
  const terakhir = entri.terakhir;
  const jumlah = formatJumlah(terakhir.qty);
  return [
    formatTanggalRiwayat(terakhir.tanggal),
    terakhir.unit || "",
    jumlah ? `${jumlah} ${terakhir.satuan || entri.qty_unit || ""}`.trim() : ""
  ]
    .filter(bagian => bagian !== "")
    .join(" · ");
}

export function keteranganRiwayatObat(
  entri: RiwayatObatEntri,
  bulan: number
): string {
  const bagian: string[] = [];

  if (entri.terakhir.status !== "diberikan" && entri.terakhir_diberikan) {
    bagian.push(
      `Terakhir diberikan farmasi ${formatTanggalRiwayat(
        entri.terakhir_diberikan
      )}`
    );
  }

  if (entri.jumlah_diberikan > 1) {
    bagian.push(`Diberikan ${entri.jumlah_diberikan}× dalam ${bulan} bulan`);
  }

  return bagian.join(" · ");
}

export function keteranganSalinRiwayatObat(entri: RiwayatObatEntri): string {
  const salin = entri.salin;

  if (!salin.tanggal || salin.tanggal === entri.terakhir.tanggal) {
    return "";
  }

  const isi = salin.aturan_pakai ? `: ${salin.aturan_pakai}` : "";
  return `Salin memakai resep ${formatTanggalRiwayat(salin.tanggal)}${isi}`;
}

export function cocokPencarianRiwayatObat(
  entri: RiwayatObatEntri,
  kata: string | null
): boolean {
  const dicari = String(kata || "")
    .trim()
    .toLowerCase();

  if (!dicari) {
    return true;
  }

  return [entri.obat_nama, entri.gnr].filter(Boolean).some(teks =>
    String(teks)
      .toLowerCase()
      .includes(dicari)
  );
}

export function barisDariRiwayatObat(
  entri: RiwayatObatEntri
): BarisResepTerstruktur {
  const salin = entri.salin;
  const signaBebas = salin.signa_bebas ? String(salin.signa_bebas) : null;

  return barisBaru({
    obat_id: entri.obat_id,
    obat_nama: entri.obat_nama,
    qty: salin.qty,
    qty_unit: salin.qty_unit || entri.qty_unit,
    frekuensi_id: signaBebas ? null : salin.frekuensi_id,
    rute_id: salin.rute_id,
    dosis_qty: salin.dosis_qty,
    satuan_dosis_id: salin.satuan_dosis_id,
    signa_bebas: signaBebas,
    aturan_tambahan_ids: (salin.aturan_tambahan_ids || []).map(id =>
      Number(id)
    ),
    _signa_lain: !!signaBebas,
    _dosis_lama: salin.dosis_qty ? null : salin.dosis_lama,
    _disalin: `Disalin dari riwayat ${formatTanggalRiwayat(
      salin.tanggal || entri.terakhir.tanggal
    )}`,
    _fokus: true
  });
}
